/*
  Tearstones header presentation and mobile focus

  Measures the navigation row and enables overlay only at the homepage's top.
  Moves focus into the native mobile menu and connects its external trigger's
  Escape key to the native close method. Native controllers retain menu layers,
  sticky behavior, dialogs and scroll lock. Section edits release all listeners.
*/
const immersiveHeaders = new Map();
const mobileMenuFocus = new Map();

function connectHeader(header) {
  const group = header.closest('#header-group');
  if (!group) return;
  const lifecycle = new AbortController();
  let frame = 0;
  let observedHero = null;

  const sync = () => {
    frame = 0;
    if (!header.isConnected) return;
    const hero = document.querySelector('.ts-home-hero');
    const row = header.getBoundingClientRect();
    const height = `${row.height}px`;
    if (group.style.getPropertyValue('--ts-header-row-height') !== height) {
      group.style.setProperty('--ts-header-row-height', height);
    }
    if (hero !== observedHero) {
      if (observedHero) observer?.unobserve(observedHero);
      observedHero = hero;
      if (hero) observer?.observe(hero);
    }
    const bounds = hero?.getBoundingClientRect();
    const overlapsHero = Boolean(bounds && bounds.top < row.bottom && bounds.bottom > row.bottom + 1);
    header.dataset.tsOverHero = String(window.scrollY <= 8 && overlapsHero);
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(sync);
  };
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
  observer?.observe(header);
  observer?.observe(group);
  window.addEventListener('scroll', schedule, { passive: true, signal: lifecycle.signal });
  window.addEventListener('resize', schedule, { passive: true, signal: lifecycle.signal });
  window.addEventListener('pageshow', schedule, { signal: lifecycle.signal });
  document.addEventListener('shopify:section:load', schedule, { signal: lifecycle.signal });
  document.addEventListener('shopify:section:unload', schedule, { signal: lifecycle.signal });
  sync();
  immersiveHeaders.set(header, () => {
    lifecycle.abort();
    observer?.disconnect();
    cancelAnimationFrame(frame);
    group.style.removeProperty('--ts-header-row-height');
    immersiveHeaders.delete(header);
  });
}

function initializeHeaders() {
  for (const [header, disconnect] of immersiveHeaders) {
    if (!header.isConnected) disconnect();
  }
  document.querySelectorAll('.header[data-ts-immersive="true"]').forEach((header) => {
    if (!immersiveHeaders.has(header)) connectHeader(header);
  });
}

initializeHeaders();
document.addEventListener('shopify:section:load', initializeHeaders);
document.addEventListener('shopify:section:unload', (event) => {
  for (const [header, disconnect] of immersiveHeaders) {
    if (event.target.contains(header)) disconnect();
  }
});


/*
  Focus after native mobile opening and Escape from its external trigger
*/
function connectMobileMenuFocus(menu) {
  if (!menu.panel || !menu.trigger || typeof menu.closeMenu !== 'function') return;
  const lifecycle = new AbortController();
  let frame = 0;

  const focusOpenedMenu = () => {
    cancelAnimationFrame(frame);
    frame = 0;
    if (!menu.isOpen) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      if (!menu.isConnected || !menu.isOpen || menu.desktopQuery?.matches) return;
      const candidates = menu.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
        'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const first = Array.from(candidates).find((candidate) =>
        candidate.tabIndex >= 0 && !candidate.closest('[hidden], [inert]') &&
        candidate.getAttribute('aria-hidden') !== 'true' &&
        candidate.getAttribute('aria-disabled') !== 'true' &&
        getComputedStyle(candidate).visibility !== 'hidden');
      first?.focus({ preventScroll: true });
    });
  };

  const observer = new MutationObserver(focusOpenedMenu);
  observer.observe(menu, { attributes: true, attributeFilter: ['data-open'] });
  menu.trigger.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !menu.isOpen) return;
    event.preventDefault();
    event.stopPropagation();
    menu.closeMenu(true);
  }, { signal: lifecycle.signal });
  focusOpenedMenu();
  mobileMenuFocus.set(menu, () => {
    lifecycle.abort();
    observer.disconnect();
    cancelAnimationFrame(frame);
    mobileMenuFocus.delete(menu);
  });
}

function initializeMobileMenuFocus() {
  for (const [menu, disconnect] of mobileMenuFocus) {
    if (!menu.isConnected) disconnect();
  }
  document.querySelectorAll('mobile-menu-dialog').forEach((menu) => {
    if (!mobileMenuFocus.has(menu)) connectMobileMenuFocus(menu);
  });
}

customElements.whenDefined('mobile-menu-dialog').then(initializeMobileMenuFocus);
document.addEventListener('shopify:section:load', initializeMobileMenuFocus);
document.addEventListener('shopify:section:unload', (event) => {
  for (const [menu, disconnect] of mobileMenuFocus) {
    if (event.target.contains(menu)) disconnect();
  }
});
